/**
 * Journal composer: the product's central gesture.
 *
 * Create: visible on Today (and calendar days) with the cursor ready.
 * Edit: the same surface preloaded with the entry (route /entry/$id).
 */
import { createEffect, createMemo, createSignal, For, on, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Camera, Loader2, RefreshCw, Sparkles, X } from "@fleet/icons";
import {
  cn,
  countWords,
  ENTRY_TEMPLATES,
  isMoodValue,
  type MoodValue,
  nextPrompt,
  parseMoodTags,
  promptOfTheDay,
} from "@journal/shared";
import type { JournalEntry } from "@journal/powersync";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { MoodPicker } from "./mood-picker";
import { TagPicker } from "./tag-picker";
import { useEntryMutations } from "../hooks/use-entry-mutations";
import { usePhotoUpload } from "../hooks/use-photo-upload";
import { useJournalData } from "../hooks/use-journal-data";

const MAX_TITLE_LENGTH = 200;
const MIN_TEXTAREA_PX = 120;

/** Localizable template prompts (D5): key → lines. */
function templateBody(key: string, t: (message: MessageDescriptor) => string): string {
  if (key === "gratitude") {
    return [t(msg`Today I am grateful for…`), `1. `, `2. `, `3. `].join("\n");
  }
  if (key === "three_good_things") {
    return [t(msg`Three good things that happened today:`), `1. `, `2. `, `3. `].join("\n");
  }
  return "";
}

function templateLabel(key: string, t: (message: MessageDescriptor) => string): string {
  if (key === "gratitude") {
    return t(msg`Gratitude`);
  }
  if (key === "three_good_things") {
    return t(msg`3 good things`);
  }
  return key;
}

/** Localized text for a daily prompt (key → question). */
function promptText(key: string, t: (message: MessageDescriptor) => string): string {
  switch (key) {
    case "prompt.grateful_today": {
      return t(msg`What are you grateful for today?`);
    }
    case "prompt.made_you_smile": {
      return t(msg`What made you smile today?`);
    }
    case "prompt.learned_recently": {
      return t(msg`What's something you learned recently?`);
    }
    case "prompt.looking_forward": {
      return t(msg`What are you looking forward to?`);
    }
    case "prompt.proud_of": {
      return t(msg`What's something you're proud of?`);
    }
    case "prompt.let_go_of": {
      return t(msg`What do you need to let go of?`);
    }
    case "prompt.small_win": {
      return t(msg`What was a small win today?`);
    }
    case "prompt.someone_kind": {
      return t(msg`Who was kind to you lately, and how?`);
    }
    case "prompt.recharges_you": {
      return t(msg`What recharges you when you're drained?`);
    }
    case "prompt.challenge_faced": {
      return t(msg`What challenge did you face, and how did you handle it?`);
    }
    case "prompt.favourite_moment": {
      return t(msg`What was your favourite moment today?`);
    }
    case "prompt.tell_younger_self": {
      return t(msg`What would you tell your younger self?`);
    }
    case "prompt.want_to_remember": {
      return t(msg`What do you want to remember about today?`);
    }
    case "prompt.feeling_right_now": {
      return t(msg`How are you really feeling right now?`);
    }
    case "prompt.goal_this_week": {
      return t(msg`What's one goal for this week?`);
    }
    case "prompt.beautiful_lately": {
      return t(msg`What's something beautiful you noticed lately?`);
    }
    default: {
      return t(msg`What's on your mind today?`);
    }
  }
}

interface EntryComposerProps {
  /** Journal day to which the new entry belongs. */
  dateKey: () => string;
  /** Edit mode: existing entry to preload. */
  entry?: () => JournalEntry | null;
  /** Callback after a successful save (creation or edit). */
  onSaved?: (entryId: string) => void;
  /** Autofocus the body on mount. */
  autofocus?: boolean;
}

export function EntryComposer(props: EntryComposerProps) {
  const { t } = useI18n();
  const { drizzle } = useJournalData();
  const { createEntry, updateEntry } = useEntryMutations();
  const { uploadPhotos, isUploading } = usePhotoUpload();

  const [title, setTitle] = createSignal("");
  const [body, setBody] = createSignal("");
  const [mood, setMood] = createSignal<MoodValue | null>(null);
  const [moodTags, setMoodTags] = createSignal<string[]>([]);
  const [tagIds, setTagIds] = createSignal<string[]>([]);
  const [templateKey, setTemplateKey] = createSignal<string | null>(null);
  // Daily prompt (deterministic by date); "another question" rotates the catalog.
  // oxlint-disable-next-line solid/reactivity -- intentional: initial value only
  const [promptKey, setPromptKey] = createSignal<string>(promptOfTheDay(props.dateKey()));
  const [pendingFiles, setPendingFiles] = createSignal<File[]>([]);
  const [saving, setSaving] = createSignal(false);
  // Counter that is incremented ONLY AFTER the local write has been
  // confirmed (post-`await`). On creation, the success signal is the composer
  // being emptied (`reset()`), but on edit the composer keeps the text, so
  // without this there was no observable marker that it "has persisted".
  // By exposing it as `data-saved-tick`, a consumer (e.g. e2e or a future exit
  // guard) can wait for REAL persistence before navigating instead of trusting
  // the textarea value (which reflects the local signal immediately, BEFORE
  // the OPFS write has drained — and a hard navigation in between lost
  // it).
  const [savedTick, setSavedTick] = createSignal(0);

  let bodyRef: HTMLTextAreaElement | undefined;
  let fileRef: HTMLInputElement | undefined;

  const isEdit = () => Boolean(props.entry?.());

  // Preload in edit mode (reactive to entry changes).
  createEffect(
    on(
      () => props.entry?.(),
      (entry) => {
        if (!entry) {
          return;
        }
        setTitle(entry.title ?? "");
        setBody(entry.body ?? "");
        setMood(isMoodValue(entry.mood) ? entry.mood : null);
        setMoodTags(parseMoodTags(entry.mood_tags));
        setTemplateKey(entry.template_key ?? null);
        queueMicrotask(autosize);
      },
    ),
  );

  const autosize = () => {
    if (!bodyRef) {
      return;
    }
    bodyRef.style.height = "auto";
    bodyRef.style.height = `${Math.max(bodyRef.scrollHeight, MIN_TEXTAREA_PX)}px`;
  };

  createEffect(() => {
    if (props.autofocus) {
      queueMicrotask(() => bodyRef?.focus());
    }
  });

  const applyTemplate = (key: string) => {
    if (templateKey() === key) {
      setTemplateKey(null);
      return;
    }
    setTemplateKey(key);
    if (body().trim().length === 0) {
      setBody(templateBody(key, t));
      queueMicrotask(() => {
        autosize();
        bodyRef?.focus();
        bodyRef?.setSelectionRange(bodyRef.value.length, bodyRef.value.length);
      });
    }
  };

  // The daily prompt is offered only when CREATING with an empty body (we do
  // not overwrite what was written). Using it seeds the question as the first line
  // and leaves the cursor ready below it.
  const showPrompt = createMemo(() => !isEdit() && body().trim().length === 0);

  const usePrompt = () => {
    if (body().trim().length > 0) {
      return;
    }
    setBody(`${promptText(promptKey(), t)}\n\n`);
    queueMicrotask(() => {
      autosize();
      bodyRef?.focus();
      bodyRef?.setSelectionRange(bodyRef.value.length, bodyRef.value.length);
    });
  };

  const shufflePrompt = () => {
    setPromptKey((current) => nextPrompt(current, props.dateKey()));
  };

  const addFiles = (files: FileList | null) => {
    if (!files) {
      return;
    }
    const images = [...files].filter((f) => f.type.startsWith("image/"));
    setPendingFiles((prev) => [...prev, ...images]);
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const reset = () => {
    setTitle("");
    setBody("");
    setMood(null);
    setMoodTags([]);
    setTagIds([]);
    setTemplateKey(null);
    setPendingFiles([]);
    queueMicrotask(autosize);
  };

  const handleSave = async () => {
    const text = body().trim();
    if (!text || saving()) {
      return;
    }
    setSaving(true);
    try {
      const existing = props.entry?.();
      if (existing) {
        const ok = await updateEntry(existing.id, {
          entryDate: existing.entry_date ?? props.dateKey(),
          body: text,
          title: title(),
          mood: mood(),
          moodTags: moodTags(),
        });
        if (ok) {
          await uploadPhotos(existing.id, pendingFiles());
          setPendingFiles([]);
          showToast(t(msg`Entry saved.`), "success");
          setSavedTick((n) => n + 1);
          props.onSaved?.(existing.id);
        }
        return;
      }

      const entryId = await createEntry({
        entryDate: props.dateKey(),
        body: text,
        title: title(),
        mood: mood(),
        moodTags: moodTags(),
        templateKey: templateKey(),
        tagIds: tagIds(),
      });
      if (entryId) {
        await uploadPhotos(entryId, pendingFiles());
        showToast(t(msg`Entry saved.`), "success");
        setSavedTick((n) => n + 1);
        reset();
        props.onSaved?.(entryId);
      }
    } finally {
      setSaving(false);
    }
  };

  const wordCount = () => countWords(body());

  return (
    <div
      class="flex flex-col gap-3 rounded-2xl border border-border/50 bg-card/70 p-4 shadow-sm backdrop-blur-sm"
      data-testid="entry-composer"
      data-saved-tick={savedTick()}
    >
      {/* Templates (only when creating, with an empty body or active template) */}
      <Show when={!isEdit()}>
        <div class="flex flex-wrap items-center gap-1.5">
          <Sparkles class="h-3.5 w-3.5 text-muted-foreground/60" />
          <For each={[...ENTRY_TEMPLATES]}>
            {(template) => (
              <button
                type="button"
                data-testid={`template-${template.key}`}
                class={cn(
                  "rounded-full border border-transparent bg-muted/40 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors duration-120 hover:bg-muted hover:text-foreground",
                  templateKey() === template.key && "border-primary/30 bg-primary/12 text-primary",
                )}
                onClick={() => {
                  applyTemplate(template.key);
                }}
              >
                {templateLabel(template.key, t)}
              </button>
            )}
          </For>
        </div>
      </Show>

      {/* Daily prompt: expires the blank page (only when creating, with an empty body) */}
      <Show when={showPrompt()}>
        <div
          class="flex items-center gap-2 rounded-xl border border-primary/15 bg-primary/[0.06] px-3 py-2"
          data-testid="daily-prompt"
        >
          <button
            type="button"
            class="min-w-0 flex-1 text-left text-[13px] italic leading-snug text-foreground/80 transition-colors hover:text-foreground"
            data-testid="daily-prompt-use"
            onClick={usePrompt}
            title={t(msg`Use this prompt`)}
          >
            {promptText(promptKey(), t)}
          </button>
          <button
            type="button"
            class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground/60 transition-colors hover:bg-primary/10 hover:text-primary"
            data-testid="daily-prompt-shuffle"
            aria-label={t(msg`Another prompt`)}
            title={t(msg`Another prompt`)}
            onClick={shufflePrompt}
          >
            <RefreshCw class="h-3.5 w-3.5" />
          </button>
        </div>
      </Show>

      <input
        type="text"
        data-testid="composer-title"
        class="w-full bg-transparent font-display text-lg font-semibold text-foreground outline-none placeholder:font-sans placeholder:text-base placeholder:font-normal placeholder:text-muted-foreground/50"
        placeholder={t(msg`Title (optional)`)}
        value={title()}
        maxLength={MAX_TITLE_LENGTH}
        onInput={(e) => setTitle(e.currentTarget.value)}
      />

      <textarea
        ref={(el) => {
          bodyRef = el;
          queueMicrotask(autosize);
        }}
        data-testid="composer-body"
        class="w-full resize-none bg-transparent font-serif text-base leading-[1.75] text-foreground outline-none placeholder:italic placeholder:text-muted-foreground/50"
        style={{ "min-height": `${MIN_TEXTAREA_PX}px` }}
        placeholder={t(msg`How was your day?`)}
        value={body()}
        onInput={(e) => {
          setBody(e.currentTarget.value);
          autosize();
        }}
      />

      <MoodPicker
        mood={mood}
        onMoodChange={setMood}
        moodTags={moodTags}
        onMoodTagsChange={setMoodTags}
      />

      <Show when={!isEdit()}>
        <TagPicker selectedIds={tagIds} onChange={setTagIds} />
      </Show>

      {/* Fotos pendientes de subir */}
      <Show when={pendingFiles().length > 0}>
        <div class="flex flex-wrap gap-2">
          <For each={pendingFiles()}>
            {(file, i) => (
              <div class="relative">
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  class="h-16 w-16 rounded-lg border border-border/40 object-cover"
                />
                <button
                  type="button"
                  class="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow"
                  aria-label={t(msg`Remove photo`)}
                  onClick={() => {
                    removePendingFile(i());
                  }}
                >
                  <X class="h-3 w-3" />
                </button>
              </div>
            )}
          </For>
        </div>
      </Show>

      <div class="flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            data-testid="composer-photo-input"
            onChange={(e) => {
              addFiles(e.currentTarget.files);
              e.currentTarget.value = "";
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            class="gap-1.5 text-muted-foreground"
            data-testid="composer-add-photo"
            onClick={() => fileRef?.click()}
          >
            <Camera class="h-4 w-4" />
            {t(msg`Add photo`)}
          </Button>
          <span class="text-[11px] text-muted-foreground/50">
            {t(msg`{count} words`, { count: wordCount() })}
          </span>
        </div>

        <Button
          size="sm"
          data-testid="composer-save"
          disabled={body().trim().length === 0 || saving() || isUploading() || !drizzle()}
          onClick={() => {
            void handleSave();
          }}
        >
          <Show when={saving() || isUploading()}>
            <Loader2 class="mr-1.5 h-3.5 w-3.5 animate-spin" />
          </Show>
          {isEdit() ? t(msg`Save changes`) : t(msg`Save entry`)}
        </Button>
      </div>
    </div>
  );
}
